import React, { useState } from "react";
import "./ContactFormApp.scss";
import { useTranslation } from "react-i18next";
import { useForm, UseFormReturn } from "react-hook-form";
import { ContactForm } from "./types/ContactForm";
import contactFormService from "./services/ContactFormService";
import { Finish } from "./modules/Finish";
import { ValidationError } from "./types/ValidationError";
import "react-datepicker/dist/react-datepicker-min.module.css";
import { DropdownItem } from "spot-component-library/lib/components/dropdown/DropdownItem";
import { SPOTInput } from "spot-component-library/lib/components/form/spotInput/SPOTInput";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import { SPOTTextArea } from "spot-component-library/lib/components/form/textarea/SPOTTextArea";
import AppContainer from "spot-component-library/lib/components/layout/app-container/AppContainer";
import { Dropdown, DropdownResult } from "spot-component-library/lib/components/dropdown/Dropdown";
import { RateAgreementQuestionType } from "./types/RateAgreementQuestionType";
import { RateAgreementSubForm } from "./modules/RateAgreementSubForm";
import { RateAgreementFormData } from "./types/RateAgreementFormData";
import { SPOTNotification } from "spot-component-library/lib/components/notification/SPOTNotification";
import { TopicType } from "./types/TopicType";
import { Spinner } from "spot-component-library/lib/components/basic/spinner/Spinner";
import { initDefaultLanguageAfterAppLoad } from "../i18n";
import { Etracker } from "spot-component-library/lib/components/etracker/Etracker";
import { readLanguageFromCookie } from "./utils/helper";
import { useTenant } from "./contexts/TenantContext";
import { Tenant } from "./services/TenantService/TenantService";
import { createLoginBundleScript } from "./utils/login-module";

export const ContactFormApp = function () {
    const tenant = useTenant();

    const [contactForm, setContactForm] = useState<ContactForm>(new ContactForm());
    const { t } = useTranslation();
    const [showFinish, setShowFinish] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [loadingButton, setLoadingButton] = React.useState<boolean>(false);
    const [rateAgreementTeaserEnabled] = React.useState<boolean>(true);
    const [debtNumberValidated, setDebtNumberValidated] = React.useState<string>();
    const [isPageLoaded, setIsPageLoaded] = React.useState(false);
    const [topicTypeList, setTopicTypeList] = React.useState<string[]>([]);

    const [rateAgreementFormData, setRateAgreementFormData] = useState<RateAgreementFormData>({});

    const RETE_AGREEMENT_SCRIPT_ID = "rateagreement-bundle-script";

    const form: UseFormReturn = useForm({
        mode: "all",
    });

    const teaserTextTranslate = t("TEASER_TEXT");

    React.useEffect(() => {
        if (tenant) {
            initData(tenant)
                .then(() => setIsPageLoaded(true))
                .finally(() => {
                    setIsPageLoaded(true);
                    createLoginBundleScript(debtLoginListener);
                });
        }
    }, [tenant]);

    React.useEffect(() => {
        contactForm.debtNumber = debtNumberValidated;
    }, [debtNumberValidated]);

    const initData = async (tenant: Tenant) => {
        await initDefaultLanguageAfterAppLoad(tenant);

        const [debtNumber, topicTypes] = await Promise.all([contactFormService.getDebtNo(), contactFormService.getTopicTypes()]);
        if (debtNumber && !document.getElementById(RETE_AGREEMENT_SCRIPT_ID)) {
            createRateAgreementBundleScript();
        }

        setTopicTypeList(topicTypes);
    };

    const debtLoginListener = (debtId: string) => {
        resetTopicSpecificSubForm();
        setErrorMessage(undefined);
        updateTeaser(debtId);
        setDebtNumberValidated(debtId);
    };

    const createRateAgreementBundleScript = () => {
        if (rateAgreementTeaserEnabled) {
            const script = document.createElement("script");
            script.id = RETE_AGREEMENT_SCRIPT_ID;
            script.src = "/services/rateagreement/rateagreement-bundle.js";
            script.async = true;

            document.body.appendChild(script);
        }
    };

    const updateTeaser = (debtNumber: string) => {
        if (!document.getElementById(RETE_AGREEMENT_SCRIPT_ID)) {
            createRateAgreementBundleScript();
        }

        const teaserEvent = new CustomEvent("debtnumberchange", {
            detail: {
                debtnumber: debtNumber,
            },
            bubbles: true,
            cancelable: true,
            composed: false,
        });

        window.dispatchEvent(teaserEvent);
    };

    const handleTopicOnChange = (e: any) => {
        const dropDownResult = e as DropdownResult;
        const contactFormTmp: ContactForm = { ...contactForm };
        contactFormTmp.topic = dropDownResult.result.value;
        contactFormTmp.topicTextValue = dropDownResult.result.label;
        form.setValue(dropDownResult.name, dropDownResult.result.value, { shouldValidate: true });
        setContactForm(contactFormTmp);
        resetTopicSpecificSubForm();
    };

    const resetTopicSpecificSubForm = () => {
        setRateAgreementFormData({});
    };

    const handleFormOnChange = async (e: any) => {
        const target = e.target;
        const name = target.name;
        const value = target.value;

        const contactFormTmp: any = { ...contactForm };
        contactFormTmp[name] = value;

        setContactForm(contactFormTmp);
    };

    const isCustomMessageRequired = (): boolean => {
        return (
            contactForm.topic !== "RATE_AGREEMENT" || (contactForm.topic === "RATE_AGREEMENT" && rateAgreementFormData.questionType === RateAgreementQuestionType.FURTHER_QUESTIONS)
        );
    };

    const showDefaultInputForm = (): boolean => {
        let returnValue: boolean;
        switch (contactForm.topic) {
            case undefined:
            case null:
                returnValue = false;
                break;
            case TopicType.RATE_AGREEMENT_QUESTION_WITHOUT_FEATURE:
                returnValue = true;
                break;
            case "RATE_AGREEMENT":
                returnValue = !!rateAgreementFormData.questionType;
                break;
            default:
                returnValue = true;
        }

        return returnValue;
    };

    const showTopicSpecificForm = (): boolean => {
        if (contactForm.topic === "RATE_AGREEMENT") {
            return (!!contactForm.debtNumber && form.formState.isValid) || !!rateAgreementFormData.questionType;
        }
        return false;
    };

    const translateTopic = (topicKey: string): string => {
        switch (topicKey) {
            case TopicType.QUESTION:
                return "CONTACT_FORM.TOPIC_OPTIONS.GENERAL_QUESTIONS";
            case TopicType.DEFERMENT_OF_PAYMENT:
                return "APP_ROOT.PAYMENT_TYPE.BANK_TRANSFER";
            case TopicType.QUESTION_ABOUT_PAYMENTS:
                return "CONTACT_FORM.TOPIC_OPTIONS.PAYMENTS_QUESTIONS";
            case TopicType.DEBT_LINEUP:
                return "CONTACT_FORM.TOPIC_OPTIONS.DEBT_AGGREGATION";
            case TopicType.INSOLVENCY_PROCEEDINGS:
                return "CONTACT_FORM.TOPIC_OPTIONS.INSOLVENCY_PROCEEDING";
            case TopicType.RATE_AGREEMENT:
                return "CONTACT_FORM.TOPIC_OPTIONS.RATE_AGREEMENT";
            case TopicType.RATE_AGREEMENT_QUESTION_WITHOUT_FEATURE:
                return "CONTACT_FORM.TOPIC_OPTIONS.RATE_AGREEMENT";
            case TopicType.ORDER_DOCUMENTS:
                return "CONTACT_FORM.TOPIC_OPTIONS.ORDER_DOCUMENTS";
            case TopicType.COMPLAINT:
                return "CONTACT_FORM.TOPIC_OPTIONS.COMPLAINT";
            case TopicType.COMPLAINT_BAFIN:
                return "CONTACT_FORM.TOPIC_OPTIONS.COMPLAINT_BAFIN";
            case TopicType.DEBT_COUNSELLING:
                return "CONTACT_FORM.TOPIC_OPTIONS.DEBT_COUNSELLING";
            default:
                return "";
        }
    };

    const submitData = async () => {
        setErrorMessage(undefined);
        const result = await form.trigger();

        if (result) {
            const isRateChangeInputValid = rateAgreementFormData.newRateAmount || rateAgreementFormData.newPaymentDate;
            if (
                !isRateChangeInputValid &&
                contactForm.topic === "RATE_AGREEMENT" &&
                rateAgreementFormData.questionType === RateAgreementQuestionType.EDIT_EXISTING_RATE_AGREEMENT
            ) {
                form.setError("newRateAmount", { type: "rateChangeValidation", message: t("VALIDATION.RATE_AMOUNT_DATE_INVALID") });
                form.setError("newPaymentDate", { type: "rateChangeValidation", message: t("VALIDATION.RATE_AMOUNT_DATE_INVALID") });
                form.setFocus("newRateAmount");
                return;
            }

            if (contactForm.topic === TopicType.RATE_AGREEMENT_QUESTION_WITHOUT_FEATURE) {
                contactForm.topic = TopicType.RATE_AGREEMENT;
            }

            contactForm.timeStamp = new Date().getTime().toString();
            setLoadingButton(true);
            contactFormService
                .createContactForm(contactForm, rateAgreementFormData)
                .then(() => {
                    setShowFinish(true);
                    try {
                        // @ts-ignore
                        ET_Event.click("NeuesKontaktformularErfolgreich", "");
                    } catch {
                        console.warn("ETC missing!");
                    }
                })
                .catch((error: { invalidArguments: { forEach: (arg0: (argument: string) => void) => void }; message: any }) => {
                    if (error instanceof ValidationError) {
                        error.invalidArguments.forEach((argument: string) => form.setError(argument, {}));
                        setErrorMessage(t("ERROR.SERVER_VALIDATION_MESSAGE"));
                    } else {
                        switch (error.message) {
                            case "400":
                                setErrorMessage(t("ERROR.MESSAGE_400"));
                                break;
                            case "403":
                                setErrorMessage(t("ERROR.MESSAGE_403"));
                                break;
                            case "429":
                                setErrorMessage(t("ERROR.MESSAGE_429"));
                                break;
                            default: {
                                setErrorMessage(t("ERROR.SERVER_MESSAGE_DEFAULT"));
                                break;
                            }
                        }
                    }
                    setLoadingButton(false);
                });
        }
    };

    return (
        <AppContainer header={{ menuLoginEnabled: true }}>
            {tenant?.name && <Etracker pageName={"Kontakt"} popto={"1500"} tenant={tenant.name} language={readLanguageFromCookie()} />}
            <div className={"contact-data-container"}>
                {tenant?.name != "ch-eos" && <div id={"rateagreement-teaser"} data-teaser-text={`${teaserTextTranslate}`}></div>}
                {!showFinish && (
                    <>
                        {isPageLoaded && (
                            <div>
                                <Panel header={t("CONTACT_FORM.HEADER")}>
                                    {/* Debt id login is loaded from login service to this id */}
                                    <div id="login-debt-id"></div>

                                    <label className="input-field-label">
                                        <b>{t("CONTACT_FORM.TOPIC")}</b>
                                    </label>
                                    <Dropdown
                                        placeholder={t("CONTACT_FORM.TOPIC")}
                                        name={"topic"}
                                        dataTest={"wishes"}
                                        register={form.register as any}
                                        withBorder={true}
                                        required={true}
                                        onChange={handleTopicOnChange}>
                                        {topicTypeList.map((topic) => (
                                            <DropdownItem key={topic} value={topic} label={t(translateTopic(topic))} />
                                        ))}
                                    </Dropdown>
                                </Panel>
                                {showTopicSpecificForm() && (
                                    <>
                                        {contactForm.topic === "RATE_AGREEMENT" && (
                                            <RateAgreementSubForm validatedDebtNumber={debtNumberValidated} onChange={setRateAgreementFormData} form={form} />
                                        )}
                                    </>
                                )}
                                {showDefaultInputForm() && (
                                    <Panel>
                                        <SPOTInput
                                            label={t("CONTACT_FORM.Vorname")}
                                            onChange={handleFormOnChange}
                                            id={"firstname"}
                                            name={"firstname"}
                                            placeholder={t("CONTACT_FORM.Vorname")}
                                            maxLength={100}
                                            register={form.register as any}
                                            required={false}
                                            pattern={{
                                                value: /^[^=<>&?%(){};@$~|,+!#[\]':^´/`"\\0-9]+$/,
                                                message: t("VALIDATION.MESSAGE_LETTERS"),
                                            }}
                                            errors={form.formState.errors}
                                            defaultValue={contactForm.firstname}
                                        />
                                        <SPOTInput
                                            label={t("CONTACT_FORM.Nachname")}
                                            id={"lastname"}
                                            name={"lastname"}
                                            onChange={handleFormOnChange}
                                            placeholder={t("CONTACT_FORM.Nachname")}
                                            maxLength={100}
                                            register={form.register as any}
                                            required={false}
                                            pattern={{
                                                value: /^[^=<>&?%(){}@!$;~|,+#/`[\]':^´"\\0-9]+$/,
                                                message: t("VALIDATION.MESSAGE_LETTERS"),
                                            }}
                                            errors={form.formState.errors}
                                            defaultValue={contactForm.lastname}
                                        />
                                        <SPOTInput
                                            label={t("CONTACT_FORM.Email")}
                                            id={"email"}
                                            onChange={handleFormOnChange}
                                            name={"email"}
                                            placeholder={t("CONTACT_FORM.Email")}
                                            maxLength={50}
                                            register={form.register as any}
                                            required={t("VALIDATION.MESSAGE_REQUIRED.EMAIL") as string}
                                            pattern={{
                                                // RFC822 Standard
                                                value: /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/,
                                                message: t("VALIDATION.EMAIL_INVALID"),
                                            }}
                                            errors={form.formState.errors}
                                            defaultValue={contactForm.email}
                                        />
                                        <SPOTInput
                                            label={t("CONTACT_FORM.Telefon")}
                                            id={"phoneNumber"}
                                            name={"phoneNumber"}
                                            onChange={handleFormOnChange}
                                            placeholder={t("CONTACT_FORM.Telefon")}
                                            register={form.register as any}
                                            maxLength={21}
                                            required={false}
                                            minLength={5}
                                            pattern={{
                                                value: /^\+*\d{5,21}$/,
                                                message: t("VALIDATION.TELEPHONE_INVALID"),
                                            }}
                                            errors={form.formState.errors}
                                            defaultValue={contactForm.phoneNumber}
                                        />
                                        <label className="input-field-label">
                                            <b>{t("CONTACT_FORM.Textarea") + (isCustomMessageRequired() ? "*" : "")}</b>
                                        </label>
                                        <SPOTTextArea
                                            id={"message"}
                                            name={"message"}
                                            register={form.register as any}
                                            maxLength={1500}
                                            rows={7}
                                            showNumberOfCharacter={true}
                                            required={isCustomMessageRequired() ? `${t("VALIDATION.MESSAGE_REQUIRED.MESSAGE")}` : false}
                                            onChange={handleFormOnChange}
                                            placeholder={t("CONTACT_FORM.Textarea") + (isCustomMessageRequired() ? "*" : "")}
                                            defaultValue={contactForm.message}
                                            pattern={{
                                                value: /^[^=<>&%(){};~|+'´`"\\]{1,1500}$/,
                                                message: t("VALIDATION.SPECIAL_CHARACTER_MESSAGE"),
                                            }}
                                            errors={form.formState.errors}
                                        />
                                    </Panel>
                                )}
                                <Panel>
                                    <div>
                                        <p>* {t("CONTACT_FORM.RequiredField")}</p>
                                        {errorMessage && <SPOTNotification text={errorMessage} type={"error"} active={true} />}
                                    </div>
                                </Panel>
                                {showDefaultInputForm() && (
                                    <div className={"contact-send-button-block"}>
                                        <SPOTButton
                                            id={"send_contact_form"}
                                            type="primary"
                                            onClick={submitData}
                                            disabled={!form.formState.isValid}
                                            label={loadingButton ? t("CONTACT_FORM.SubmitButtonLoading") : t("CONTACT_FORM.SubmitButton")}
                                            isLoading={loadingButton}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <Spinner isLoading={!isPageLoaded} type="page" color={"red"} />
                    </>
                )}
                {showFinish && <Finish />}
            </div>
        </AppContainer>
    );
};
